/**
 * @features/dashboard/components/mobile/MobileMoreSheet.jsx
 * --------------------------------------------------------
 * Full-screen sheet that exposes the entire role-aware navigation
 * config on mobile. Solves the mobile feature-discoverability gap
 * documented in `/app/memory/AUDIT_DASHBOARD_ECOSYSTEM.md` §10
 * (15 admin / 9 dealer / 8 builder destinations had no mobile
 * entry point before this component).
 *
 * Behaviour:
 *   • Opens via the hamburger button in MobileDashboardHeader.
 *   • Renders ALL items from navForPersona(persona).
 *   • Includes a typeahead search input (filters items by label).
 *   • Selecting an item navigates AND closes the sheet.
 *   • Footer shows the current user + Sign-out button.
 *
 * v2.71.38 — Initial. Reusable across all 5 personas.
 */

import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, LogOut, X } from "lucide-react";
import {
  Sheet, SheetContent, SheetTitle, SheetDescription,
} from "../../../../components/ui/sheet";
import { useAuth } from "../../../../lib/AuthContext";
import { useDashboardNav } from "../../hooks/useDashboardNav";

export function MobileMoreSheet({ open, onOpenChange, persona }) {
  const items = useDashboardNav(persona);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((it) => it.label.toLowerCase().includes(needle));
  }, [items, q]);

  const handleSelect = (to) => {
    onOpenChange(false);
    setQ("");
    // Smooth-feel delay so the sheet's exit animation begins before
    // routing reflow.
    setTimeout(() => nav(to), 60);
  };

  const handleSignOut = async () => {
    onOpenChange(false);
    try { await logout(); } catch (_) { /* ignore */ }
    nav("/");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-full sm:max-w-sm p-0 flex flex-col"
        data-testid="mobile-more-sheet"
      >
        <SheetTitle className="sr-only">Dashboard navigation</SheetTitle>
        <SheetDescription className="sr-only">
          Full list of dashboard destinations for your role.
        </SheetDescription>

        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-[#E4E4E7]">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#0055FF]">
                {persona ? persona.toUpperCase() : "ACCOUNT"}
              </div>
              <div className="font-heading text-base font-semibold truncate" data-testid="more-sheet-user">
                {user?.name || user?.email || "Guest"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-9 h-9 -mr-1 flex items-center justify-center rounded-sm text-[#52525B] hover:bg-[#F4F4F5]"
              aria-label="Close menu"
              data-testid="more-sheet-close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search menu…"
              className="w-full h-10 pl-9 pr-3 bg-white border border-[#E4E4E7] rounded-sm text-[13px] focus:outline-none focus:border-[#0055FF]"
              data-testid="more-sheet-search"
            />
          </div>
        </div>

        {/* Items */}
        <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Dashboard navigation">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-[13px] text-[#71717A]">
              No menu items match "{q}".
            </div>
          ) : (
            <ul className="space-y-0.5">
              {filtered.map((item) => {
                const Icon = item.icon;
                const active = pathname === item.to || pathname.startsWith(item.to + "/");
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(item.to)}
                      className={[
                        "w-full flex items-center gap-3 px-3 py-3 rounded-sm",
                        "text-[14px] font-medium tracking-tight text-left transition-colors",
                        "min-h-[48px]",
                        active
                          ? "bg-[#EEF4FF] text-[#0055FF]"
                          : "text-[#27272A] hover:bg-[#F4F4F5] active:bg-[#F4F4F5]",
                      ].join(" ")}
                      data-testid={`more-nav-${item.id}`}
                    >
                      {Icon ? <Icon size={17} strokeWidth={1.75} className="flex-shrink-0" /> : null}
                      <span className="flex-1 truncate">{item.label}</span>
                      {typeof item.badge === "function" && item.badge() ? (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 bg-[#DC2626] text-white text-[10px] font-semibold rounded-full">
                          {item.badge()}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* Footer */}
        {user && (
          <div className="border-t border-[#E4E4E7] px-3 py-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
            <button
              type="button"
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-sm border border-[#E4E4E7] text-[13px] font-semibold text-[#DC2626] hover:bg-[#FEF2F2]"
              data-testid="more-sheet-signout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default MobileMoreSheet;
